import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import type {
  ApiISODateString,
  ApiISODateTimeNoTzString,
} from 'api/types/ApiTypedDate';
import {
  FormattedDate,
  FormattedDateTime,
  SingleDayDateTime,
} from 'components/Date/DateUtil';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import { DetailListSection } from 'components/DetailsList/Section/DetailListSection';
import { useI18n } from 'i18n/hooks/useI18n';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';

export const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const [dollars] = parts;
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

type Props = {
  areasOfFocus: ApiAreaOfFocus[];
  startDate: ApiISODateString | null;
  endDate: ApiISODateString | null;
  startsLocal: ApiISODateTimeNoTzString | null;
  endsLocal: ApiISODateTimeNoTzString | null;
  feeAmount: string | null;
  feeRequired: boolean;
  timezone: string | null;
  welcomeGroups: boolean;
  welcomeFamilies: boolean;
  detailsWheelchairAccessible: boolean;
};

export function EventDetailsList({
  areasOfFocus,
  startDate,
  endDate,
  startsLocal,
  endsLocal,
  feeAmount,
  feeRequired,
  timezone,
  welcomeGroups,
  welcomeFamilies,
  detailsWheelchairAccessible,
}: Props) {
  const { i18n } = useI18n();

  return (
    <DetailsList>
      {startDate && endDate && startDate === endDate && (
        <DetailsListItem
          title={i18n('Date')}
          iconName="calendar"
          value={
            <SingleDayDateTime
              startDate={startsLocal ?? startDate}
              endDate={endsLocal ?? endDate}
              showStartTime={Boolean(startsLocal)}
              showEndTime={Boolean(endsLocal)}
              timezone={timezone}
            />
          }
        />
      )}

      {startDate && startDate !== endDate && (
        <DetailsListItem
          title={i18n('Start Date')}
          iconName="hourglass"
          value={
            startsLocal ? (
              <FormattedDateTime dateTime={startsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={startDate} />
            )
          }
        />
      )}

      {endDate && endDate !== startDate && (
        <DetailsListItem
          title={i18n('End Date')}
          iconName="hourglass"
          value={
            endsLocal ? (
              <FormattedDateTime dateTime={endsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={endDate} />
            )
          }
        />
      )}

      {feeRequired && (
        <DetailsListItem
          title={i18n('Cost')}
          iconName="coin"
          value={`${i18n(
            'Paid Event',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      {areasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={i18n('Cause Areas')}
          value={areasOfFocus.map((item) => AREAS_OF_FOCUS[item]).join(', ')}
        />
      )}

      <DetailListSection title={i18n('Participation')}>
        {welcomeGroups && (
          <DetailsListItem iconName="group" value={i18n('Groups Welcome')} />
        )}

        {welcomeFamilies && (
          <DetailsListItem iconName="family" value={i18n('Families Welcome')} />
        )}

        {detailsWheelchairAccessible && (
          <DetailsListItem
            iconName="wheelchair"
            value={i18n('Wheelchair Accessible')}
          />
        )}
      </DetailListSection>
    </DetailsList>
  );
}
